import { StyleSheet, View } from 'react-native'
import { Chip, Surface, Text, useTheme } from 'react-native-paper'
import { formatPrice } from '@/lib/billing'
import type { PlanInfo, SubscriptionInfo } from '@/types/billing'

interface PlanCardProps {
    plan: PlanInfo | undefined
    subscription: SubscriptionInfo | undefined
}

export function PlanCard({ plan, subscription }: PlanCardProps) {
    const theme = useTheme()

    if (!plan) {
        return null
    }

    const getStatusColor = () => {
        switch (subscription?.status) {
            case 'active':
                return theme.colors.primary
            case 'trialing':
                return theme.colors.tertiary
            case 'past_due':
                return theme.colors.error
            case 'canceled':
                return theme.colors.outline
            default:
                return theme.colors.primary
        }
    }

    const getStatusLabel = () => {
        switch (subscription?.status) {
            case 'active':
                return 'Active'
            case 'trialing':
                return 'Trial'
            case 'past_due':
                return 'Past Due'
            case 'canceled':
                return 'Canceled'
            default:
                return 'Active'
        }
    }

    return (
        <Surface style={styles.container} elevation={1}>
            <View style={styles.header}>
                <View style={styles.titleRow}>
                    <Text variant="headlineSmall" style={styles.planName}>
                        {plan.name}
                    </Text>
                    <Chip
                        mode="flat"
                        compact
                        style={{ backgroundColor: getStatusColor() }}
                        textStyle={{ color: theme.colors.onPrimary }}
                    >
                        {getStatusLabel()}
                    </Chip>
                </View>
                <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                    {plan.description}
                </Text>
            </View>

            <View style={styles.priceRow}>
                {plan.price_monthly > 0 ? (
                    <>
                        <Text variant="displaySmall" style={styles.price}>
                            {formatPrice(plan.price_monthly)}
                        </Text>
                        <Text variant="bodyMedium" style={{ color: theme.colors.onSurfaceVariant }}>
                            /month
                        </Text>
                    </>
                ) : (
                    <Text variant="displaySmall" style={styles.price}>
                        Free
                    </Text>
                )}
            </View>

            {subscription?.cancel_at_period_end && (
                <View style={[styles.warning, { backgroundColor: theme.colors.errorContainer }]}>
                    <Text variant="bodySmall" style={{ color: theme.colors.onErrorContainer }}>
                        Your subscription will be canceled at the end of the billing period
                    </Text>
                </View>
            )}
        </Surface>
    )
}

const styles = StyleSheet.create({
    container: {
        borderRadius: 12,
        padding: 16,
        marginVertical: 8,
    },
    header: {
        gap: 8,
    },
    titleRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    planName: {
        fontWeight: 'bold',
    },
    priceRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        gap: 4,
        marginTop: 16,
    },
    price: {
        fontWeight: 'bold',
    },
    warning: {
        marginTop: 16,
        padding: 12,
        borderRadius: 8,
    },
})
